import type { AuditFields } from '@crm/shared';

/**
 * MESSAGE INFO — info polimorfica associata a un Message.
 *
 * Modello 1:N tra Message e MessageInfo: ogni messaggio espone un array
 * `infos[]` eager-load (vedi Message.infos in messageTypes). Le info
 * rappresentano coppie chiave/valore derivate da entita' di dominio via
 * il pattern polimorfico Liferay classNameId/classPK.
 *
 * Campi server-managed (non modificabili dal client):
 *   - messageInfoId, messageId
 *   - label (chiave semantica, es. "sender-department")
 *   - labelTranslated (risolta dal server con LanguageUtil + Accept-Language)
 *   - className (FQN Java dell'entita' correlata, es. "it.dvel.crm.core.model.Business"),
 *     classPK (PK dell'entita' correlata)
 *   - audit fields
 *
 * Campi editabili dal client (endpoint PATCH /message/{mid}/info/{iid}):
 *   - value (max 4000 char)
 *   - priority (int, ordinamento crescente)
 */
export interface MessageInfo extends AuditFields {
  messageInfoId: number;
  messageId: number;
  label: string;
  labelTranslated: string;
  value: string;
  className: string;
  classPK: number;
  priority: number;
}

/** Payload PATCH /message/{mid}/info/{iid} — solo campi editabili. */
export interface MessageInfoUpdatePayload {
  value: string;
  priority: number;
}

/**
 * True se la info punta a un'entita' polimorfica valida: richiede sia
 * `className` non vuoto sia `classPK > 0`. Usato per decidere se creare
 * Relationship inferite (0 / '' sono i valori sentinella della serializzazione
 * DTO "nessun collegamento polimorfico").
 */
export function hasEntityRef(info: MessageInfo): boolean {
  return info.className !== '' && info.classPK > 0;
}

/**
 * Split delle info di un messaggio in "popolate" (value non vuoto,
 * sezione "Informazioni estratte") e "mancanti" (value vuoto, sezione
 * "Informazioni mancanti"). Entrambi gli array sono ordinati per priority
 * ascendente, coerente con la UI.
 */
export function splitInfos(infos: MessageInfo[]): {
  populated: MessageInfo[];
  missing: MessageInfo[];
} {
  const sorted = [...infos].sort((a, b) => a.priority - b.priority);
  return {
    populated: sorted.filter((i) => i.value !== ''),
    missing: sorted.filter((i) => i.value === ''),
  };
}

/**
 * Label che rappresentano valori booleani ('true' / 'false'). La UI renderizza
 * il value localizzato via i18n key `messageInfo.values.true|false` ("Si"/"No"
 * in italiano). In edit mode l'utente vede comunque la stringa raw.
 */
/**
 * Label del flag "azienda già a sistema", prodotto da BusinessDataMessageExtractor.
 *
 * Ha una costante propria perché è l'unica info che la UI **non** mostra come il backend
 * l'ha scritta: il valore persistito è uno snapshot preso all'ingestione e non viene più
 * ricalcolato, quindi un'azienda censita dopo l'arrivo dell'email vi risulta assente per
 * sempre. La smart inbox ricalcola la risposta dal vivo (TS-82) e questa costante è ciò
 * che tiene insieme i punti che devono saperlo.
 */
export const BUSINESS_EXISTS_LABEL = 'businessExists';

/**
 * Label del link Google Maps del cantiere, prodotto da TechnicalDataMessageExtractor.
 *
 * Ha una costante propria perché è l'unico valore estratto che non si legge in riga: è
 * una URL con le coordinate, lunga e senza spazi, che la smart inbox tronca — e per cui
 * quindi offre il copia negli appunti.
 */
export const GMAP_LABEL = 'gMap';

export const BOOLEAN_LABELS: ReadonlySet<string> = new Set([
  'photovoltaic',
  'solarThermal',
  'plannedInstallation',
  'obstacles',
  'laying',
  'technicalReport',
  BUSINESS_EXISTS_LABEL,
  'isNew',
]);

/**
 * Label con valore enum a stringhe fisse (PARTIAL/TOTAL, WOOD/CONCRETE/...).
 * La UI renderizza il value via i18n key `messageInfo.values.<VALUE>` (es.
 * "PARTIAL" -> "Parziale", "WOOD" -> "Legno"). In edit mode l'utente vede
 * comunque la stringa raw.
 */
export const ENUM_LABELS: ReadonlySet<string> = new Set([
  'material',
  'materialType',
  'accessType',
  'securityLevel',
]);

/**
 * Label con value numerico + unita' di misura. Mapping label -> i18n key
 * `messageInfo.units.<unit>` (es. "cm"). La UI renderizza "8 cm" / "60 cm".
 */
export const NUMERIC_UNIT_LABELS: ReadonlyMap<string, string> = new Map([
  ['width', 'cm'],
  ['joistSpacing', 'cm'],
]);

/**
 * Estrae il "nome corto" di un extractor dal suo FQN Java. Rimuove il pacchetto
 * e il suffisso `MessageExtractor`. Es:
 *   "it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor" -> "LoadBearing"
 *   "it.dvel.crm.core.internal.mail.extractor.SummaryMessageExtractor"   -> "Summary"
 *
 * Usato per costruire la chiave i18n `messageInfo.groups.<shortName>`.
 * Ritorna `_unknown` se className e' null/empty (fallback per le info di
 * sistema senza extractor associato).
 */
export function extractorShortName(className: string | null | undefined): string {
  if (!className) return '_unknown';
  const lastDot = className.lastIndexOf('.');
  const simpleName = lastDot >= 0 ? className.substring(lastDot + 1) : className;
  const SUFFIX = 'MessageExtractor';
  return simpleName.endsWith(SUFFIX)
    ? simpleName.substring(0, simpleName.length - SUFFIX.length)
    : simpleName;
}

/**
 * Restituisce la label tradotta per la UI. Ordine di precedenza:
 *   1. i18n client `messageInfo.labels.<label>` (preferito; aggiunto in locales/it+en).
 *   2. {@link MessageInfo.labelTranslated} (popolato dal backend via LanguageUtil,
 *      tipicamente la key raw se non c'e' entry in Language_*.properties).
 *   3. {@link MessageInfo.label} raw.
 *
 * Il fallback su `labelTranslated` permette di mostrare almeno qualcosa anche per
 * label nuove (es. extractor di terze parti) non ancora tradotte client-side.
 */
export function getLocalizedLabel(
  info: MessageInfo,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const key = `messageInfo.labels.${info.label}`;
  const translated = t(key, { defaultValue: '' });
  if (translated && translated !== key) return translated;
  return info.labelTranslated || info.label;
}

/**
 * Formatta il value di una MessageInfo per la presentazione (view mode). Prima di
 * tutto cerca una traduzione legata alla label, `messageInfo.values.<label>.<VALUE>`:
 * e' la strada dei plugin, che non possono aggiungere le loro label agli insiemi
 * qui sotto (es. Pollini: `messageInfo.values.fpAdmissionOutcome.ADMITTED` = «Ammesso»).
 * Il core non conosce le label dei plugin: basta che la traduzione ci sia. Poi le 3
 * trasformazioni note:
 *   - {@link BOOLEAN_LABELS}: "true"/"false" → i18n `messageInfo.values.true|false`.
 *   - {@link ENUM_LABELS}: "PARTIAL"/"WOOD"/... → i18n `messageInfo.values.<VALUE>`.
 *   - {@link NUMERIC_UNIT_LABELS}: "8" → "8 cm" (unit via i18n `messageInfo.units.<u>`).
 *   - Altri: raw {@code info.value}.
 *
 * In edit mode il chiamante passa comunque il value raw a {@link EditableField}
 * (questo helper serve solo per la presentazione).
 */
export function formatLocalizedValue(
  info: MessageInfo,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const v = info.value;
  if (v === '' || v === null || v === undefined) return '';

  // Traduzione per label: vuota se manca, e allora si prosegue come prima. Un valore
  // con un punto non trova la chiave (il punto separa i livelli), ed e' giusto cosi':
  // i valori da tradurre sono codici, non testo libero.
  const byLabelKey = `messageInfo.values.${info.label}.${v}`;
  const byLabel = t(byLabelKey, { defaultValue: '' });
  if (byLabel && byLabel !== byLabelKey) return byLabel;

  if (BOOLEAN_LABELS.has(info.label) && (v === 'true' || v === 'false')) {
    return t(`messageInfo.values.${v}`, { defaultValue: v });
  }

  if (ENUM_LABELS.has(info.label)) {
    return t(`messageInfo.values.${v}`, { defaultValue: v });
  }

  const unit = NUMERIC_UNIT_LABELS.get(info.label);
  if (unit) {
    const unitLabel = t(`messageInfo.units.${unit}`, { defaultValue: unit });
    return `${v} ${unitLabel}`;
  }

  return v;
}

/**
 * Gruppo di info prodotte da uno specifico extractor, per il rendering grouped.
 * Specchia 1:1 lo schema REST ExtractorGroup: dopo TS-10 il backend
 * (DTOConverterUtil.toMessage) consegna `Message.extractorInfos` gia' come
 * array di ExtractorGroup, ordinato per priority dell'extractor. Il client non
 * raggruppa piu' (la vecchia groupByExtractor e' stata rimossa): si limita a
 * iterare preservando l'ordine del server.
 */
export interface ExtractorGroup {
  /** FQN dell'extractor (null come fallback difensivo; il server invia sempre l'FQN). */
  className: string | null;
  /** Info del gruppo, gia' ordinate per `priority` MessageInfo dal server. */
  infos: MessageInfo[];
}

/**
 * Da `Message.extractorInfos` (gruppi gia' ordinati dal server) ricava i gruppi
 * di una sezione, tenendo per ciascun gruppo solo le info con value popolato
 * (`populated`) o vuoto (`missing`), e scartando i gruppi che restano senza info
 * in quella sezione. L'ordine dei gruppi e' quello del server; dentro ogni
 * gruppo le info sono ordinate per `priority` ascendente (coerente con la UI).
 */
export function groupsBySection(
  groups: ExtractorGroup[],
  section: 'populated' | 'missing',
): ExtractorGroup[] {
  return groups
    .map((group) => {
      const sorted = [...group.infos].sort((a, b) => a.priority - b.priority);
      const infos = sorted.filter((i) => (section === 'populated' ? i.value !== '' : i.value === ''));
      return { ...group, infos };
    })
    .filter((group) => group.infos.length > 0);
}
